import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1120SStockAndDebtBasis = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="S Corporation Stock & Debt Basis Guide | Form 1120-S"
          description="Learn how S corporation stock and debt basis works, IRS ordering rules, loss limitations, and distribution tax treatment for accurate Form 1120-S reporting."
          keywords=""
          canonical="resources/business-tax/form-1120-s-stock-and-debt-basis/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="1658"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">
              S Corporation Stock and Debt Basis
            </span>
          </span>{' '}
          — IRS Rules, Calculations & Loss Limitations Guide Filers
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  component="article"
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/business-tax/form-1120-s-stock-and-debt-basis/"
                  />
                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-25T09:00:00-05:00"
                      >
                        Aug 25, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-25T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 25, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    component="h2"
                    itemProp="headline"
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      mb: 0.8,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    S Corporation Stock and Debt Basis at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Understand S corporation stock and debt basis,
                        </strong>{' '}
                        including how the IRS calculates basis and why it
                        determines whether losses, distributions, and gains are
                        taxable.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Learn the IRS ordering rules
                        </strong>{' '}
                        for adjusting stock and debt basis, how shareholder loss
                        limitations work, and when suspended losses can be
                        deducted.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Avoid common basis calculation mistakes
                        </strong>{' '}
                        by understanding distribution rules, debt basis
                        requirements, and annual basis tracking for Form 1120-S
                        compliance.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  S corporation shareholders must track stock basis and debt
                  basis every year to determine whether losses are deductible,
                  distributions are taxable, and gains are calculated correctly.
                  Because S corporations operate under pass-through taxation,
                  financial activity reported on{' '}
                  <Link
                    href="https://www.irs.gov/pub/irs-pdf/f1120ssk.pdf"
                    target="_blank"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    Schedule K-1
                  </Link>{' '}
                  directly affects each owner&apos;s personal return — but basis
                  calculations remain the shareholder&apos;s responsibility.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This resource explains how basis works, the IRS ordering
                  rules, and how shareholders can avoid common compliance issues
                  when{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff', fontWeight: 600 }}
                  >
                    filing Form 1120-S
                  </Link>
                  .
                </Typography>

                <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How Shareholder Loss Limitations Work in an S
                          Corporation
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#irsrule" sx={{ color: '#0202ff' }}>
                          IRS Rule Highlights: What Shareholders Must Know
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#stock" sx={{ color: '#0202ff' }}>
                          Stock Basis — The Foundation of S Corporation Tax
                          Reporting
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#ordering" sx={{ color: '#0202ff' }}>
                          IRS Ordering Rules for Annual Basis Adjustments
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#distribution" sx={{ color: '#0202ff' }}>
                          Distribution Tax Treatment — Why Basis Comes First
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#debt" sx={{ color: '#0202ff' }}>
                          Debt Basis — Secondary Support for Loss Deductions
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#suspended" sx={{ color: '#0202ff' }}>
                          Suspended Losses and Carryforwards
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#pro-rata" sx={{ color: '#0202ff' }}>
                          Pro-Rata Allocation of Losses
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#real" sx={{ color: '#0202ff' }}>
                          Real-World Example: How Basis Changes Over Time
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#advanced" sx={{ color: '#0202ff' }}>
                          Advanced Compliance Considerations
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How Shareholder Loss Limitations Work in an S Corporation
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  S corporation income and deductions flow through to
                  shareholders rather than being taxed at the corporate level.
                  While this structure allows owners to potentially use business
                  losses, the IRS applies multiple limitation layers before a
                  loss can be claimed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Shareholders must pass the following tests in order:
                </Typography>
                <Box component="ol" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Stock and debt basis limitation </li>
                  <li>At-risk limitation </li>
                  <li>Passive activity limitation </li>
                  <li>Excess business loss limitation </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Failing any level stops the deduction — even if the Schedule
                  K-1 shows a loss.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="irsrule"
                >
                  IRS Rule Highlights: What Shareholders Must Know
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    A Schedule K-1 reports allocated items, not taxable results.
                  </li>
                  <li>
                    Distributions are tax-free only up to available stock basis.
                  </li>
                  <li>
                    Debt basis applies only when shareholders lend funds
                    directly to the corporation.
                  </li>
                  <li>
                    The corporation does not track shareholder basis — the
                    shareholder must maintain it independently.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  These principles shape every basis calculation performed
                  during the year.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="stock"
                >
                  Stock Basis — The Foundation of S Corporation Tax Reporting
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Stock basis represents a shareholder&apos;s investment in
                  ownership shares. It begins when stock is acquired through
                  contributions or purchase and changes annually based on
                  company activity.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  When Stock Basis Is Required
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Shareholders must calculate stock basis when:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Claiming losses or deductions</li>
                  <li>Receiving distributions</li>
                  <li>Selling or transferring stock</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Because stock basis fluctuates annually, many shareholders use
                  Form 7203 to calculate allowable amounts.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  What Increases Stock Basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Stock basis generally increases when the corporation generates
                  economic benefit for the shareholder, such as:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Ordinary business income</li>
                  <li>Separately stated income items</li>
                  <li>Tax-exempt income</li>
                  <li>Certain depletion adjustments</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These increases expand the shareholder’s capacity to deduct
                  losses or receive tax-free distributions.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontWeight: 600,
                    fontSize: '18px !important',
                  }}
                >
                  What Reduces Stock Basis
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Stock basis decreases — but cannot go below zero — due to:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Non-dividend distributions</li>
                  <li>Non-deductible expenses</li>
                  <li>Ordinary losses and deductions</li>
                  <li>Certain depletion deductions</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Dividend distributions follow separate rules and are typically
                  reported outside the Schedule K-1 framework.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="ordering"
                >
                  IRS Ordering Rules for Annual Basis Adjustments
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  The sequence used to adjust stock basis each year is extremely
                  important. The IRS requires adjustments to be applied in this
                  order:
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Increase basis for income items
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Reduce basis for distributions
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Reduce basis for non-deductible expenses
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Reduce basis for losses and deductions
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Applying these steps incorrectly can cause distributions to
                  appear taxable or losses to be overstated.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="distribution"
                >
                  Distribution Tax Treatment — Why Basis Comes First
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Most S corporation distributions are classified as
                  non-dividend distributions. These payments reduce stock basis
                  rather than generating immediate taxable income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Key principles:{' '}
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 12 }}>
                    Only stock basis determines whether a distribution is
                    tax-free.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Debt basis does not prevent distribution taxation.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Distributions exceeding stock basis generally become capital
                    gains.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This rule surprises many shareholders who assume distributions
                  are always tax-free.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="debt"
                >
                  Debt Basis — Secondary Support for Loss Deductions
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Debt basis exists only when a shareholder personally lends
                  money to the S corporation. Guarantees of third-party loans do
                  not create basis.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Debt basis becomes relevant when:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>Stock basis reaches zero</li>
                  <li>
                    Additional losses remain after stock basis is exhausted
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Losses applied against debt basis reduce the loan’s remaining
                  basis. If the corporation later repays that loan, part of the
                  repayment may be taxable.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="suspended"
                >
                  Suspended Losses and Carryforwards
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  When losses exceed available stock and debt basis, they are
                  not lost immediately. Instead, they become suspended and carry
                  forward indefinitely.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Suspended losses keep their original tax character.</li>
                  <li>They become deductible when basis increases.</li>
                  <li>
                    They must be tracked separately from current-year items.
                  </li>
                  <li>
                    Disposing of all stock permanently eliminates unused
                    suspended losses.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This carryforward system allows shareholders to benefit from
                  future profitability.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="pro-rata"
                >
                  Pro-Rata Allocation of Losses
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If multiple deduction types exceed available basis, the
                  allowable portion must be allocated proportionally among them.
                  Shareholders cannot choose which losses to claim first.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Additionally, non-deductible expenses reduce basis before loss
                  deductions are applied — even though they do not reduce
                  taxable income.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="real"
                >
                  Real-World Example: How Basis Changes Over Time
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  A typical shareholder scenario demonstrates how ordering rules
                  apply:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>Beginning basis increases when income is reported.</li>
                  <li>Distributions reduce basis first.</li>
                  <li>Non-deductible expenses reduce basis next.</li>
                  <li>
                    Losses are allowed only up to remaining basis, with excess
                    suspended.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  In future years, income restores basis and unlocks previously
                  suspended losses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  This annual recalculation is why basis tracking must be
                  consistent and accurate.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="advanced"
                >
                  Advanced Compliance Considerations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Experienced S corporation shareholders and tax professionals
                  often focus on the following technical details:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>A loan guarantee does not create debt basis.</li>
                  <li>
                    Repayment of reduced-basis loans may generate taxable
                    income.
                  </li>
                  <li>Losses exceeding basis carry forward indefinitely.</li>
                  <li>
                    Basis must be recalculated every year, even without
                    distributions.
                  </li>
                  <li>
                    Financial ability to fund shareholder loans may be
                    scrutinized during audits.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Ignoring these details can lead to disallowed deductions or
                  unexpected tax exposure.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to apply what you&apos;ve learned?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File your Form 1120-S with TaxZerone and simplify your S
              corporation reporting from start to finish.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1120sguide`}
              >
                Start Filing Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1120SStockAndDebtBasis;
